// src/App.js - Updated to restrict driver access by adding requiredRole="Admin" to driver-accessible routes
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme/theme';

// Context and Layout
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Auth pages
import LoginPage from './pages/auth/LoginPage';

// Legal pages
import TermsAndConditionsPage from './pages/legal/TermsAndConditionsPage';

// Company pages
import CompaniesPage from './pages/company/CompaniesPage';
import CompanyDetailsPage from './pages/company/CompanyDetailsPage';
import CompanyCreatePage from './pages/company/CompanyCreatePage';
import CompanyEditPage from './pages/company/CompanyEditPage';

// Site pages
import SitesPage from './pages/site/SitesPage';
import SiteDetailsPage from './pages/site/SiteDetailsPage';
import SiteCreatePage from './pages/site/SiteCreatePage';
import SiteEditPage from './pages/site/SiteEditPage';
import SiteStandaloneCreatePage from './pages/site/SiteStandAloneCreatePage';

// Tariff pages
import TariffsPage from './pages/tariff/TariffsPage';
import TariffDetailsPage from './pages/tariff/TariffDetailsPage';
import TariffCreatePage from './pages/tariff/TariffCreatePage';
import TariffEditPage from './pages/tariff/TariffEditPage';

// Driver pages
import DriversPage from './pages/driver/DriversPage';
import DriverDetailsPage from './pages/driver/DriverDetailsPage';
import DriverCreatePage from './pages/driver/DriverCreatePage';
import DriverEditPage from './pages/driver/DriverEditPage';

// Driver Group pages
import DriverGroupsPage from './pages/driver_group/DriverGroupsPage';
import DriverGroupDetailsPage from './pages/driver_group/DriverGroupDetailsPage';
import DriverGroupCreatePage from './pages/driver_group/DriverGroupCreatePage';
import DriverGroupEditPage from './pages/driver_group/DriverGroupEditPage';

// Charger pages
import ChargersPage from './pages/charger/ChargersPage';
import ChargerDetailsPage from './pages/charger/ChargerDetailsPage';
import ChargerCreatePage from './pages/charger/ChargerCreatePage';
import ChargerEditPage from './pages/charger/ChargerEditPage';
import ChargerSessionsPage from './pages/charger/ChargerSessionsPage';

// Session pages
import SessionsPage from './pages/session/SessionsPage';
import SessionDetailsPage from './pages/session/SessionDetailsPage';
import SessionPaymentPage from './pages/session/SessionPaymentPage';

// Site Group pages
import SiteGroupsPage from './pages/sitegroup/SiteGroupsPage';
import SiteGroupCreatePage from './pages/sitegroup/SiteGroupCreatePage';
import SiteGroupEditPage from './pages/sitegroup/SiteGroupEditPage';

// Payment pages
import PaymentPage from './pages/payment/PaymentPage';
import PaymentTransactionDetailsPage from './pages/payment/PaymentTransactionDetailsPage';

// User pages
import UsersPage from './pages/user/UsersPage';
import UserDetailsPage from './pages/user/UserDetailsPage';
import UserEditPage from './pages/user/UserEditPage';
import UserCreatePage from './pages/user/UserCreatePage';

import ApiTest from './components/ApiTest'; // Keep for testing
import ChargersDriverPage from './pages/charger/ChargersDriverPage';
import DriverSessionsPage from './pages/driver/DriverSessionsPage';
import DriverPaymentsPage from './pages/driver/DriverPaymentsPage';
import DriverProfilePage from './pages/driver/DriverProfilePage';

// Redirect components
import AdminRedirectComponent from './components/auth/AdminRedirectComponent';
import DriverRedirectComponent from './components/auth/DriverRedirectComponent';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/tsacs" element={<TermsAndConditionsPage />} />
              
              {/* QR Login routes - WITHOUT Layout */}
              <Route path="/chargers-driver-page" element={
                <ProtectedRoute>
                  <ChargersDriverPage />
                </ProtectedRoute>
              } />
              
              {/* Protected routes with Layout */}
              <Route path="/*" element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      {/* Dashboard - Only for SuperAdmin and Driver, redirect Admin to Sites */}
                      <Route path="/" element={
                        <AdminRedirectComponent>
                          <DriverRedirectComponent>
                            <CompaniesPage />
                          </DriverRedirectComponent>
                        </AdminRedirectComponent>
                      } />
                      
                      {/* Company routes - Only for SuperAdmin (KEEP UNCHANGED) */}
                      <Route path="/companies" element={
                        <ProtectedRoute requiredRole="SuperAdmin">
                          <CompaniesPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/companies/new" element={
                        <ProtectedRoute requiredRole="SuperAdmin">
                          <CompanyCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/companies/:id" element={
                        <ProtectedRoute requiredRole="SuperAdmin">
                          <CompanyDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/companies/:id/edit" element={
                        <ProtectedRoute requiredRole="SuperAdmin">
                          <CompanyEditPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/companies/:companyId/sites/new" element={
                        <ProtectedRoute requiredRole="SuperAdmin">
                          <SiteCreatePage />
                        </ProtectedRoute>
                      } />
                      
                      {/* Tariff routes - RESTRICT DRIVERS */}
                      <Route path="/tariffs" element={
                        <ProtectedRoute requiredRole="Admin">
                          <TariffsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/tariffs/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <TariffCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/tariffs/:id" element={
                        <ProtectedRoute requiredRole="Admin">
                          <TariffDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/tariffs/:id/edit" element={
                        <ProtectedRoute requiredRole="Admin">
                          <TariffEditPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/companies/:companyId/tariffs/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <TariffCreatePage />
                        </ProtectedRoute>
                      } />
                      
                      {/* Site routes - RESTRICT DRIVERS */}
                      <Route path="/sites" element={
                        <ProtectedRoute requiredRole="Admin">
                          <SitesPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/sites/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <SiteStandaloneCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/sites/:id" element={
                        <ProtectedRoute requiredRole="Admin">
                          <SiteDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/sites/:id/edit" element={
                        <ProtectedRoute requiredRole="Admin">
                          <SiteEditPage />
                        </ProtectedRoute>
                      } />
                      
                      {/* Charger routes - RESTRICT DRIVERS */}
                      <Route path="/chargers" element={
                        <ProtectedRoute requiredRole="Admin">
                          <ChargersPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/sites/:siteId/chargers/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <ChargerCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/chargers/:id" element={
                        <ProtectedRoute requiredRole="Admin">
                          <ChargerDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/chargers/:id/edit" element={
                        <ProtectedRoute requiredRole="Admin">
                          <ChargerEditPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/chargers/:id/sessions" element={
                        <ProtectedRoute requiredRole="Admin">
                          <ChargerSessionsPage />
                        </ProtectedRoute>
                      } />
                      
                      {/* Session routes - More specific routes first */}
                      <Route path="/sessions/:sessionId/payment" element={
                        <ProtectedRoute requiredRole="Driver">
                          <SessionPaymentPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/sessions/:id" element={
                        <ProtectedRoute requiredRole="Driver">
                          <SessionDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/sessions" element={
                        <ProtectedRoute requiredRole="Admin">
                          <SessionsPage />
                        </ProtectedRoute>
                      } />

                      {/* Driver routes - RESTRICT DRIVERS */}
                      <Route path="/drivers" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriversPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/drivers/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriverCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/drivers/:id" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriverDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/drivers/:id/edit" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriverEditPage />
                        </ProtectedRoute>
                      } />

                      {/* Driver Group routes - RESTRICT DRIVERS */}
                      <Route path="/driver_groups" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriverGroupsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/driver_groups/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriverGroupCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/companies/:companyId/driver_groups/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriverGroupCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/driver_groups/:id" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriverGroupDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/driver_groups/:id/edit" element={
                        <ProtectedRoute requiredRole="Admin">
                          <DriverGroupEditPage />
                        </ProtectedRoute>
                      } />

                      {/* Site Group routes - RESTRICT DRIVERS */}
                      <Route path="/site-groups" element={
                        <ProtectedRoute requiredRole="Admin">
                          <SiteGroupsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/site-groups/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <SiteGroupCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/site-groups/:id/edit" element={
                        <ProtectedRoute requiredRole="Admin">
                          <SiteGroupEditPage />
                        </ProtectedRoute>
                      } />
                      
                      {/* Payment routes - Admin and above can access (KEEP UNCHANGED) */}
                      <Route path="/payment" element={
                        <ProtectedRoute requiredRole="Admin">
                          <PaymentPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/payment/transactions/:transactionId" element={
                        <ProtectedRoute requiredRole="Driver">
                          <PaymentTransactionDetailsPage />
                        </ProtectedRoute>
                      } />

                      {/* User routes - Updated with proper role-based protection (KEEP UNCHANGED) */}
                      <Route path="/users" element={
                        <ProtectedRoute requiredRole="Admin">
                          <UsersPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/users/new" element={
                        <ProtectedRoute requiredRole="Admin">
                          <UserCreatePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/users/:id" element={
                        <ProtectedRoute requiredRole="Admin">
                          <UserDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/users/:id/edit" element={
                        <ProtectedRoute requiredRole="Admin">
                          <UserEditPage />
                        </ProtectedRoute>
                      } />

                      <Route path="/driver-sessions" element={
                        <ProtectedRoute requiredRole="Driver">
                          <DriverSessionsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/driver-payments" element={
                        <ProtectedRoute requiredRole="Driver">
                          <DriverPaymentsPage />
                        </ProtectedRoute>
                      } />

                      <Route path="/driver-profile" element={
                        <ProtectedRoute requiredRole="Driver">
                          <DriverProfilePage />
                        </ProtectedRoute>
                      } />

                      {/* Test route - SuperAdmin only (KEEP UNCHANGED) */}
                      <Route path="/test" element={
                        <ProtectedRoute requiredRole="SuperAdmin">
                          <ApiTest />
                        </ProtectedRoute>
                      } />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              } />
            </Routes>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;